import { useEffect, useRef, useState } from "react";
import {
  CandlestickSeries, ColorType, CrosshairMode, HistogramSeries, LineSeries, LineStyle, createChart, createSeriesMarkers,
  type AutoscaleInfo, type IPriceLine, type ISeriesApi, type SeriesMarker, type Time, type UTCTimestamp,
} from "lightweight-charts";
import { cn } from "@/lib/utils";
import { THEME_EVENT } from "@/lib/theme";
import { emaSeries } from "../chart/indicators";
import { BandPrimitive } from "../chart/BandPrimitive";
import { themeColor } from "../chart/themeColor";
import { pricePrecision } from "../desk/deskData";
import type { ChartLine, ChartMark, ChartSpec, ChartZone, Tone } from "./types";

const TONE_VAR: Record<Tone, [string, string]> = {
  profit: ["--profit", "#2BEA94"],
  loss: ["--loss", "#FF5470"],
  brand: ["--brand", "#D1FE17"],
  warning: ["--warning", "#FFB224"],
  muted: ["--muted", "#9CA3AF"],
};
const EMA_VAR: Record<20 | 50 | 200, [string, string]> = {
  20: ["--chart-ema-fast", "#38BDF8"],
  50: ["--chart-ema-slow", "#A78BFA"],
  200: ["--chart-ema-long", "#A8A29E"],
};

const tone = (t: Tone, alpha?: number) => themeColor(TONE_VAR[t][0], TONE_VAR[t][1], alpha);

interface GameChartProps {
  spec: ChartSpec;
  /** Answered: draw the answer and the candles after "now". */
  revealed: boolean;
  /** Price questions: the player's click, and where to send it. */
  picked?: number | null;
  onPick?: (price: number) => void;
  className?: string;
}

/**
 * A small read-only candle chart for one game question: the candles up to
 * "now" (dates hidden, so the moment can't be recalled), the question's
 * lines, marks and zones, and — once answered — the answer and what came
 * next, dimmed.
 */
export function GameChart({ spec, revealed, picked = null, onPick, className }: GameChartProps) {
  const box = useRef<HTMLDivElement>(null);
  const series = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const pickLine = useRef<IPriceLine | null>(null);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  const [themeKey, setThemeKey] = useState(0);

  useEffect(() => {
    const bump = () => setThemeKey(k => k + 1);
    window.addEventListener(THEME_EVENT, bump);
    return () => window.removeEventListener(THEME_EVENT, bump);
  }, []);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const shown = spec.candles;
    const after = revealed ? spec.ahead ?? [] : [];
    const all = [...shown, ...after];
    const last = shown[shown.length - 1];
    if (!last) return;

    const up = tone("profit");
    const down = tone("loss");
    const upDim = tone("profit", 0.35);
    const downDim = tone("loss", 0.35);
    const muted = themeColor("--muted", "rgba(160, 165, 180, 0.9)");
    const font = getComputedStyle(document.documentElement).getPropertyValue("--font-mono").trim() || "ui-monospace, monospace";

    const chart = createChart(el, {
      autoSize: true,
      layout: { background: { type: ColorType.Solid, color: "transparent" }, textColor: muted, fontSize: 10, fontFamily: font, attributionLogo: false },
      grid: { vertLines: { color: themeColor("--chart-grid", "rgba(255,255,255,0.04)") }, horzLines: { color: themeColor("--chart-grid", "rgba(255,255,255,0.04)") } },
      crosshair: {
        mode: CrosshairMode.Normal,
        // The time label would give the date away until the answer is in.
        vertLine: { labelVisible: revealed, color: themeColor("--muted", "rgba(255,255,255,0.25)", 0.4), style: LineStyle.LargeDashed },
        horzLine: { color: themeColor("--muted", "rgba(255,255,255,0.25)", 0.4), style: LineStyle.LargeDashed, labelBackgroundColor: themeColor("--surface-tertiary", "#2a2d38") },
      },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.1, bottom: spec.volume ? 0.24 : 0.08 } },
      timeScale: { visible: revealed, borderVisible: false, timeVisible: spec.tf !== "1d", secondsVisible: false, rightOffset: 4 },
      handleScroll: true,
      handleScale: true,
    });

    if (spec.volume) {
      const vol = chart.addSeries(HistogramSeries, { priceScaleId: "volume", priceFormat: { type: "volume" }, priceLineVisible: false, lastValueVisible: false });
      chart.priceScale("volume").applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
      vol.setData(all.map((c, i) => ({
        time: c.time as UTCTimestamp,
        value: c.volume,
        color: tone(c.close >= c.open ? "profit" : "loss", i < shown.length ? 0.4 : 0.15),
      })));
    }

    const digits = pricePrecision(last.close);
    // The price scale also takes in the question's lines and zones (a library price
    // line outside the candles' range is simply not drawn).
    const extra = [
      ...(spec.lines ?? []).map(l => l.price),
      ...(spec.zones ?? []).flatMap(z => [z.from, z.to]),
      ...(revealed ? [...(spec.answerLines ?? []).map(l => l.price), ...(spec.answerZones ?? []).flatMap(z => [z.from, z.to])] : []),
    ].filter(Number.isFinite);
    const candles = chart.addSeries(CandlestickSeries, {
      upColor: up, downColor: down, wickUpColor: up, wickDownColor: down, borderVisible: false,
      priceLineVisible: false, lastValueVisible: !revealed,
      priceFormat: { type: "price", precision: digits, minMove: 10 ** -digits },
      autoscaleInfoProvider: (base: () => AutoscaleInfo | null) => {
        const info = base();
        if (!info?.priceRange || extra.length === 0) return info;
        return {
          ...info,
          priceRange: {
            minValue: Math.min(info.priceRange.minValue, ...extra),
            maxValue: Math.max(info.priceRange.maxValue, ...extra),
          },
        };
      },
    });
    candles.setData(all.map((c, i) => {
      const bar = { time: c.time as UTCTimestamp, open: c.open, high: c.high, low: c.low, close: c.close };
      if (i < shown.length) return bar;
      const col = c.close >= c.open ? upDim : downDim;
      return { ...bar, color: col, wickColor: col };
    }));
    series.current = candles;

    for (const period of spec.emas ?? []) {
      const values = emaSeries(all.map(c => c.close), period);
      const line = chart.addSeries(LineSeries, {
        color: themeColor(EMA_VAR[period][0], EMA_VAR[period][1]),
        lineWidth: period === 200 ? 1 : 2,
        priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
        title: `EMA ${period}`,
      });
      line.setData(all.flatMap((c, i) => (values[i] == null ? [] : [{ time: c.time as UTCTimestamp, value: values[i]! }])));
    }

    const drawLine = (l: ChartLine, width: 1 | 2) =>
      candles.createPriceLine({
        price: l.price, color: tone(l.tone), lineWidth: width,
        lineStyle: l.dashed ? LineStyle.Dashed : LineStyle.Solid, axisLabelVisible: true, title: l.label,
      });
    for (const l of spec.lines ?? []) drawLine(l, 1);
    if (revealed) for (const l of spec.answerLines ?? []) drawLine(l, 2);

    const drawZone = (z: ChartZone) =>
      candles.attachPrimitive(new BandPrimitive({ low: Math.min(z.from, z.to), high: Math.max(z.from, z.to), fill: tone(z.tone, 0.14), edge: tone(z.tone, 0.55) }));
    for (const z of spec.zones ?? []) drawZone(z);
    if (revealed) for (const z of spec.answerZones ?? []) drawZone(z);

    const marks: ChartMark[] = [...(spec.marks ?? []), ...(revealed ? spec.answerMarks ?? [] : [])];
    if (marks.length > 0) {
      const toMarker = (m: ChartMark): SeriesMarker<Time> => ({
        time: m.time as UTCTimestamp,
        position: m.position === "above" ? "aboveBar" : "belowBar",
        shape: m.position === "above" ? "arrowDown" : "arrowUp",
        color: tone(m.tone),
        text: m.label,
      });
      createSeriesMarkers(candles, marks.slice().sort((a, b) => a.time - b.time).map(toMarker));
    }

    // Start on the last `focus` candles; after the answer, run on through what came next.
    const focus = Math.min(spec.focus ?? 80, shown.length);
    chart.timeScale().setVisibleLogicalRange({ from: shown.length - focus - 0.5, to: all.length - 1 + 4 });

    chart.subscribeClick(p => {
      if (!p.point || !onPickRef.current) return;
      const price = candles.coordinateToPrice(p.point.y);
      if (price !== null && Number.isFinite(price) && price > 0) onPickRef.current(price);
    });

    return () => {
      series.current = null;
      pickLine.current = null;
      chart.remove();
    };
  }, [spec, revealed, themeKey]);

  // The player's price answer, redrawn on its own so a click doesn't rebuild the chart.
  useEffect(() => {
    const s = series.current;
    if (!s) return;
    if (pickLine.current) {
      s.removePriceLine(pickLine.current);
      pickLine.current = null;
    }
    if (picked !== null) {
      pickLine.current = s.createPriceLine({
        price: picked, color: themeColor("--foreground", "#f2f2f2"), lineWidth: 2,
        lineStyle: LineStyle.Solid, axisLabelVisible: true, title: "Your answer",
      });
    }
  }, [picked, spec, revealed, themeKey]);

  return (
    <div
      ref={box}
      className={cn("h-64 w-full sm:h-80", onPick && !revealed && "cursor-crosshair", className)}
      aria-label="Price chart for this question"
      role="img"
    />
  );
}
